import React, { useMemo } from 'react';
import { PartnerDetail } from './TicketColumns/Partner';
import { canEditTicket, columnsKeys, getNextExecutionDate, isIndividualMilestoneStatusInRange, MilestoneSubStatus, ticketExtraColumns, TicketMilestoneEvents } from './TicketController';
import { getDateDifference, validateDateValue, validateResponseDate, validateStringValue } from '../../utils/common';
import moment from 'moment';
import IconToolTip from '../../common/IconToolTip';
import { getLevelId, textLabelClass, textValueClass, ticketFilterSchema } from './TicketTable';
import APIFilterPopup from '../../common/APIFilters/FilterPopup';
import TicketNumber from './TicketColumns/TicketNumber';
import { useSelector } from 'react-redux';
import OverflowTip from '../../common/OverflowTip';
import SiteColumn from './TicketColumns/Sites';
import { StatusColumn } from './TicketColumns/Status';
import Contact from './TicketColumns/Contact';
import { canManageOtherOrgs } from '../../utils/access';
import GridTable from '../../common/GridTable';
import { goingToSiteServiceRequest, reachedSiteServiceRequest, siteCompleteTicketMilestone, updateNoResponse } from '../../actions/service_request';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DateComponent } from '../../common/TableReusableColumns';
import { isMobileView } from '../../utils/utils';

const getTicketCreationDate = (createdDate) => {
    let formattedDate = getDateDifference(moment(createdDate).format('YYYY/MM/DD'), moment().format('YYYY/MM/DD'));
    let timeAgo = moment(createdDate).fromNow();

    if (formattedDate === 0) {
        let hoursAgo = moment().diff(moment(createdDate), 'hours');
        if (hoursAgo === 0) {
            let minutesAgo = moment().diff(moment(createdDate), 'minutes');
            return `${minutesAgo} Minutes Ago`;
        }
        return `${hoursAgo} Hours Ago`;
    }

    return `${formattedDate} Days Ago`;
};

const TodayStatusTable = ({ filterList, setFilterList, updateQuery, isTicketFilterListLoading, filtersActiveStatus, filterQueryObject, selectedColumns, rowsPerPage, page, openedMaintab }) => {

    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const manageOtherOrgs = canManageOtherOrgs(newLoggedUserData.actions);
    const ticketAssetRemarkLoading = useSelector(state => state.loading.ticketAssetRemarkLoading);
    const isServiceRequestListLoading = useSelector((state) => state.loading.isServiceRequestListLoading);
    const serviceRequestList = useSelector((state) => state.serviceRequest.serviceRequestList);
    const serviceRequestFilterList = useSelector((state) => state.serviceRequest.serviceRequestFilterList);
    const ticketColumns = useSelector((state) => state.utils.ticketColumns);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const memoizedServiceRequestCount = useMemo(() => {
        let c = 0;
        if (serviceRequestList?.metadata?.total_count) {
            c = serviceRequestList.metadata.total_count;
        }
        return c;
    }, [serviceRequestList?.metadata?.total_count]);


    const filterComponent = ({ headName, keysToFilter, disable, disableText }) => (
        <div className='flex text-font12'>
            {headName}
            <APIFilterPopup
                filterList={filterList}
                setFilterList={setFilterList}
                height={15}
                keysToFilter={keysToFilter}
                submit={({ payload, page, filterList }) => updateQuery({ filterSchema: ticketFilterSchema, payload, page, filterList })}
                filtersData={serviceRequestFilterList}
                filterSchema={ticketFilterSchema}
                filterQueryObject={filterQueryObject}
                isLoading={isTicketFilterListLoading}
                filtersActiveStatus={filtersActiveStatus}
                disable={disable}
                disableText={disableText}
            />
        </div>
    );


    const editTicket = (ticket) => {
        navigate(`/tickets/${ticket.id}/edit`);
    };

    const onSiteCompleteClick = ({ ticketId, milestoneId, formName }) => {
        dispatch(siteCompleteTicketMilestone({ ticketId, milestoneId, formName, refresh: false }));
    }

    const onReachedSiteClick = ({ ticketId, milestoneId, formName }) => {
        dispatch(reachedSiteServiceRequest({ ticketId, milestoneId, formName, refresh: false }));
    }

    const onGoingToSiteClick = ({ ticketId, milestoneId, formName }) => {
        dispatch(goingToSiteServiceRequest({ ticketId, milestoneId, formName, refresh: false }));
    }

    const onNoResponseClick = ({ ticketId, milestoneId, formName, event }) => {
        dispatch(updateNoResponse({ ticketId, milestoneId, event, formName }));
    }

    const getFirstNextMilestone = (milestones) => {
        return milestones[0];
    }

    const possibleColumns = {
        time: {
            head: {
                render: (headName) => {
                    return <div className='text-font12 pl-2'>{headName}</div>;
                },
                headName: 'Time',
                width: window.innerWidth * 0.07,
                key: columnsKeys.time,
                isFixed: true,
            },
            body: {
                render: (ticket) => {
                    const createdAt = validateDateValue(ticket.created_at) && validateResponseDate(ticket.created_at);
                    return (<div className='w-full flex px-4 text-font12 '>
                        <div>
                            <IconToolTip placement={'bottom'} title={`Created at`}>
                                <span className={textLabelClass}>{getTicketCreationDate(createdAt)}</span>
                            </IconToolTip>
                        </div>
                    </div>)
                },
            },
        },
        ticket_number: {
            head: {
                render: (headName, checkedTicketTrackerLocal) => {
                    return (
                        <div className='pl-2 flex items-center '>
                            {filterComponent({
                                headName,
                                keysToFilter: [ticketFilterSchema.executionDate,
                                ticketFilterSchema.ticketType],

                            })}
                        </div>
                    );
                },
                headName: 'Ticket No.',
                width: window.innerWidth * 0.08,
                isFixed: true,
                key: columnsKeys.ticket_id,
                columnRowCss: 'truncate',
            },
            body: {
                render: (ticket, rowIndex, checkedTicketTrackerLocal, handleOnChangeCheckboxLocal) => {
                    return <TicketNumber
                        ticket={ticket}
                        rowIndex={rowIndex}
                        checkedTicketTrackerLocal={checkedTicketTrackerLocal}
                        handleOnChangeCheckboxLocal={handleOnChangeCheckboxLocal}
                        ticketRemoteTerminal={false}
                        handleTicketNumberCLick={() => { }}
                        showCheckBox={false}
                    />
                }
            }
        },
        org_details: {
            head: {
                render: (headName) => {
                    const filters = [ticketFilterSchema.customer, ticketFilterSchema.endUser].filter((filter) => {
                        if (filter.filterName === ticketFilterSchema.customer.filterName) {
                            return manageOtherOrgs;
                        }
                        return true;
                    });
                    return filterComponent({
                        headName,
                        keysToFilter: filters,
                    });
                },
                headName: 'Customer Details',
                isFixed: true,
                width: window.innerWidth * 0.11,
                key: columnsKeys.customer_details,
                columnRowCss: 'truncate',
            },
            body: {
                render: (ticket) => {
                    const { ticket_owned_by, assignToScogo, signOffAccept, ticketType } = ticket;
                    return (
                        <div className='w-full'>
                            {/* {!isLoggedUserIsCustomer && ( */}
                            <span className={`max-w-[95%] break-all`}>
                                <p className={textLabelClass}>Customer </p>
                                <span className={textValueClass}>
                                    <OverflowTip textClass={`text-inherit ${textValueClass}`} someLongText={ticket.organization.name} />
                                </span>
                            </span>
                            {/* )} */}
                            {ticket.enduser_name && (
                                <span className={`max-w-[95%] break-all`}>
                                    {!manageOtherOrgs && <p className={textLabelClass}>End User </p>}
                                    <span className={textValueClass}>
                                        <OverflowTip textClass={`text-inherit ${textValueClass}`} someLongText={ticket.project_name} />
                                    </span>
                                </span>
                            )}
                            <span className={`max-w-[95%] break-all`}>
                                <p className={textLabelClass}>Project </p>
                                <span className='px-2 bg-scogoprimary text-white flex gap-2 items-center rounded-2xl w-fit max-w-11/12'>
                                    <OverflowTip textClass={`text-inherit ${textValueClass}`} someLongText={validateStringValue(ticket?.project?.name)} />
                                    {canEditTicket(ticket) && (
                                        <>
                                            <span className='text-white'>|</span>
                                            <span
                                                className='material-icons text-font12 text-white pr-1 cursor-pointer '
                                                onClick={() => {
                                                    editTicket(ticket);
                                                }}
                                            >
                                                edit
                                            </span>
                                        </>
                                    )}
                                </span>
                            </span>
                        </div>
                    );
                },
            },
        },
        location_details: {
            head: {
                render: (headName, checkedTicketTrackerLocal) => {
                    return (
                        <div className='flex items-center '>
                            {filterComponent({
                                headName,
                                keysToFilter: [
                                    ticketFilterSchema.state,
                                    ticketFilterSchema.city,
                                    ticketFilterSchema.districts,
                                    ticketFilterSchema.localities,
                                    ticketFilterSchema.region_tier,
                                ],
                            })}
                        </div>
                    );
                },
                headName: 'Site Details',
                width: window.innerWidth * 0.14,
                isFixed: true,
                columnRowCss: 'truncate',
                key: columnsKeys.site_details,
            },
            body: {
                render: (ticket, rowIndex, checkedTicketTrackerLocal, handleOnChangeCheckboxLocal) => <SiteColumn
                    ticket={ticket}
                    rowIndex={rowIndex}
                    checkedTicketTrackerLocal={checkedTicketTrackerLocal}
                    handleOnChangeCheckboxLocal={handleOnChangeCheckboxLocal}
                    handleTicketNumberCLick={() => { }}
                    showCheckBox={false}
                />,
            },
        },
        status: {
            head: {
                render: (headName) => {
                    let keysToFilter = [ticketFilterSchema.milestoneStatus];
                    return filterComponent({
                        headName,
                        keysToFilter
                    });
                },
                headName: 'Milestones',
                width: window.innerWidth * 0.07,
                hide: !selectedColumns.includes(ticketExtraColumns.current_stataus),
                key: columnsKeys.current_status,
                isFixed: true,
                columnRowCss: 'truncate',
            },
            body: {
                render: (ticket) => {
                    return <StatusColumn ticket_id={ticket.id} status={ticket.status} category={ticket.category} milestones={ticket.milestones} ticket={ticket} />;
                },
            },
        },
        contact_details: {
            head: {
                render: (headName) => {
                    return filterComponent({
                        headName,
                        keysToFilter: [ticketFilterSchema.clusters, ticketFilterSchema.psp, ticketFilterSchema.supervisor, ticketFilterSchema.agent],
                    });
                },
                headName: 'Contact Details',
                width: window.innerWidth * 0.12,
                hide: !selectedColumns.includes(ticketExtraColumns.contact_details),
                columnRowCss: 'break-all',
                key: columnsKeys.contact_details,
            },
            body: {
                render: (ticket) => {
                    return <Contact ticket={ticket} />;
                },
            },
        },
        partner_details: {
            head: {
                render: (headName) => {
                    return filterComponent({
                        headName,
                        keysToFilter: [ticketFilterSchema.partners, ticketFilterSchema.engineers, ticketFilterSchema.assignmentType],
                    });
                },
                headName: 'Partner Details',
                hide: !selectedColumns.includes(ticketExtraColumns.partner_details),
                width: window.innerWidth * 0.1,
                key: columnsKeys.partner_details,
            },
            body: {
                render: (ticket) => <PartnerDetail ticket={ticket} loading={ticketAssetRemarkLoading} />,
            },
        },

        going_to_site: {
            head: {
                render: (headName) => {
                    return <div className='text-font12'>{headName}</div>;
                },
                headName: 'Going To Site',
                width: window.innerWidth * 0.08,
                key: columnsKeys.going_to_site,
                isFixed: true,
            },
            body: {
                render: (ticket) => {
                    const ticketId = ticket.id;
                    const firstNextMilestone = getFirstNextMilestone(ticket.milestones);
                    const goingToSiteStatus = isIndividualMilestoneStatusInRange(firstNextMilestone, MilestoneSubStatus.TRACKING_REQUESTED, MilestoneSubStatus.HARD_CLOSED);
                    const findIfNoResponse = ticket?.remarks?.find(r => r.action === TicketMilestoneEvents.MILESTONE_NO_RESPONSE && r.payload.event === 'GOING_TO_SITE' && r.milestone_id === firstNextMilestone.id);

                    if (findIfNoResponse) {
                        return <div className='pl-4'>
                            <div className="text-green-500 font-medium">No</div>
                            <div className="text-font11">
                                <div>Updated by</div>
                                <div className="font-medium">{findIfNoResponse.user.display_name}</div>
                                {findIfNoResponse.updated_at && <DateComponent date={findIfNoResponse.updated_at} text={false} timeFormat='DD-MMM-YYYY' />}
                            </div>
                        </div>
                    }

                    const findYesResponse = ticket?.remarks?.find(r => r.action === TicketMilestoneEvents.MILESTONE_ENGINEER_ON_THE_WAY && r.milestone_id === firstNextMilestone.id);

                    return (
                        <div className="flex flex-col pl-4">
                            {goingToSiteStatus ? (
                                <>
                                    <div className="text-green-500 font-medium">Yes</div>
                                    {findYesResponse?.user?.display_name && (
                                        <div className="text-font11">
                                            <div>Updated by</div>
                                            <div className="font-medium">{findYesResponse?.user?.display_name}</div>
                                            {findYesResponse?.updated_at && <DateComponent date={findYesResponse?.updated_at} text={false} timeFormat='DD-MMM-YYYY' />}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex">
                                    <div className="inline-flex rounded-md overflow-hidden">
                                        <button
                                            className="px-3 py-1 bg-green-500 text-white hover:bg-green-600"
                                            onClick={() => onGoingToSiteClick({ ticketId, milestoneId: firstNextMilestone.id, formName: 'goingToSiteForm' })}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            className="px-3 py-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            onClick={() => onNoResponseClick({ ticketId, milestoneId: firstNextMilestone.id, formName: 'goingToSiteForm', event: 'GOING_TO_SITE' })}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                },
            },
        },
        reached_site: {
            head: {
                render: (headName) => {
                    return <div className='text-font12'>{headName}</div>;
                },
                headName: 'Reached Site',
                width: window.innerWidth * 0.08,
                key: columnsKeys.reached_site,
                isFixed: true,
            },
            body: {
                bodyClassName: '',
                render: (ticket) => {
                    const ticketId = ticket.id;
                    const firstNextMilestone = getFirstNextMilestone(ticket.milestones);
                    const reachedSiteStatus = isIndividualMilestoneStatusInRange(firstNextMilestone, MilestoneSubStatus.ENGINEER_AT_SITE, MilestoneSubStatus.HARD_CLOSED);

                    const findIfNoResponse = ticket?.remarks?.find(r => r.action === TicketMilestoneEvents.MILESTONE_NO_RESPONSE && r.payload.event === 'REACHED_SITE' && r.milestone_id === firstNextMilestone.id);

                    if (findIfNoResponse) {
                        return <div className='pl-4'>
                            <div className="text-green-500 font-medium">No</div>
                            <div className="text-font11">
                                <div>Updated by</div>
                                <div className="font-medium">{findIfNoResponse.user.display_name}</div>
                                {findIfNoResponse.updated_at && <DateComponent date={findIfNoResponse.updated_at} text={false} timeFormat='DD-MMM-YYYY' />}
                            </div>
                        </div>
                    }

                    const findYesResponse = ticket?.remarks?.find(r => r.action === TicketMilestoneEvents.MILESTONE_ENGINEER_AT_SITE && r.milestone_id === firstNextMilestone.id);

                    return (
                        <div className="flex flex-col pl-4">
                            {reachedSiteStatus ? (
                                <>
                                    <div className="text-green-500 font-medium">Yes</div>
                                    {findYesResponse?.user?.display_name && (
                                        <div className="text-font11">
                                            <div>Updated by</div>
                                            <div className="font-medium">{findYesResponse?.user?.display_name}</div>
                                            {findYesResponse?.updated_at && <DateComponent date={findYesResponse?.updated_at} text={false} timeFormat='DD-MMM-YYYY' />}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex">
                                    <div className="inline-flex rounded-md overflow-hidden">
                                        <button
                                            className="px-3 py-1 bg-green-500 text-white hover:bg-green-600"
                                            onClick={() => onReachedSiteClick({ ticketId, milestoneId: firstNextMilestone.id, formName: 'reachedSiteForm' })}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            className="px-3 py-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            onClick={() => onNoResponseClick({ ticketId, milestoneId: firstNextMilestone.id, formName: 'reachedSiteForm', event: 'REACHED_SITE' })}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                },
            },
        },
        site_complete: {
            head: {
                render: (headName) => {
                    return <div className='text-font12'>{headName}</div>;
                },
                headName: 'Site Complete',
                width: window.innerWidth * 0.08,
                key: columnsKeys.site_complete,
                isFixed: true,
            },
            body: {
                render: (ticket) => {
                    const ticketId = ticket?.id;
                    const firstNextMilestone = getFirstNextMilestone(ticket.milestones);
                    const siteCompleteStatus = isIndividualMilestoneStatusInRange(firstNextMilestone, MilestoneSubStatus.SITE_COMPLETED, MilestoneSubStatus.HARD_CLOSED);

                    const findIfNoResponse = ticket?.remarks?.find(r => r.action === TicketMilestoneEvents.MILESTONE_NO_RESPONSE && r.payload.event === 'SITE_COMPLETED' && r.milestone_id === firstNextMilestone.id);

                    if (findIfNoResponse) {
                        return <div className='pl-4'>
                            <div className="text-green-500 font-medium">No</div>
                            <div className="text-font11">
                                <div>Updated by</div>
                                <div className="font-medium">{findIfNoResponse.user.display_name}</div>
                                {findIfNoResponse.updated_at && <DateComponent date={findIfNoResponse.updated_at} text={false} timeFormat='DD-MMM-YYYY' />}
                            </div>
                        </div>
                    }

                    const findYesResponse = ticket?.remarks?.find(r => r.action === TicketMilestoneEvents.MILESTONE_SITE_COMPLETED && r.milestone_id === firstNextMilestone.id);

                    return (
                        <div className="flex flex-col pl-4">
                            {siteCompleteStatus ? (
                                <>
                                    <div className="text-green-500 font-medium">Yes</div>
                                    {findYesResponse?.user?.display_name && (
                                        <div className="text-font11">
                                            <div>Updated by</div>
                                            <div className="font-medium">{findYesResponse?.user?.display_name}</div>
                                            {findYesResponse?.updated_at && <DateComponent date={findYesResponse?.updated_at} text={false} timeFormat='DD-MMM-YYYY' />}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex">
                                    <div className="inline-flex rounded-md overflow-hidden">
                                        <button
                                            className="px-3 py-1 bg-green-500 text-white hover:bg-green-600"
                                            onClick={() => onSiteCompleteClick({ ticketId, milestoneId: firstNextMilestone.id, formName: 'siteCompleteForm' })}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            className="px-3 py-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            onClick={() => onNoResponseClick({ ticketId, milestoneId: firstNextMilestone.id, formName: 'siteCompleteForm', event: 'SITE_COMPLETED' })}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                },
            },
        },

    };



    let filteredTableColumnsSchema = { table: [] };
    if (ticketColumns && ticketColumns[getLevelId(openedMaintab)]) {
        let columns = ticketColumns[getLevelId(openedMaintab)]?.filter(elem => elem.is_visible)
            .map(({ key }) => possibleColumns[key])
            .filter(Boolean);

        columns = [];

        Object.entries(possibleColumns).map(([key, value]) => {
            columns.push(value);
        })
        filteredTableColumnsSchema = {
            table: columns
        };
    };
    if (isMobileView) {
        return <div className='w-full h-full overflow-y-auto mt-8'>
            {serviceRequestList?.data?.map((item, index) => {
                // let { milestone, statusToPrint } = getStatusToPrint(item)
                return <div
                    key={item?.id}
                    className={`p-4 rounded-lg shadow-md ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} 
                    border border-gray-200 m-2 hover:shadow-lg transition-shadow duration-200`}
                // onClick={() => handleTicketNumberCLick(item)}
                >
                    <div className="flex justify-between items-start mb-2">
                        {item?.ticket_number &&
                            <div className='text-xl font-bold text-gray-800'>
                                {item?.ticket_number}
                            </div>
                        }
                        <DateComponent
                            className="text-scogoorange font-medium"
                            date={item.created_at}
                            text={false}
                            timeFormat='DD MMM YYYY'
                        />
                    </div>

                    <div className="space-y-2 mb-3">
                        {item?.requester?.display_name &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Requested By:</span>
                                <span className="text-gray-700 font-medium">{item?.requester?.display_name}</span>
                            </div>
                        }

                        {item?.location?.external_id &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Site ID:</span>
                                <span className="text-gray-700 font-medium">{item?.location?.external_id}</span>
                            </div>
                        }

                        {item?.project?.name &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Project:</span>
                                <span className="text-gray-700 font-medium">{item?.project?.name}</span>
                            </div>
                        }

                        {item?.location?.address &&
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500">Address:</span>
                                <span className="text-gray-700 font-medium truncate">{item?.location?.address}</span>
                            </div>
                        }
                    </div>

                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
                        {/* <StatusLabel
                            onClick={(e) => {
                                e.stopPropagation();
                                let approveReject = false;
                                if (milestone) {
                                    approveReject = isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.SIGN_OFF_REQUESTED, MilestoneSubStatus.SIGN_OFF_ACCEPTED);
                                }
                                if (approveReject) {
                                    navigate(`${ticket.path}/${item?.id}/edit-payout?source=ticket?milestone_id=${milestone.id}`)
                                }
                            }}
                            labelClass={`${MilestoneSubStatusColor[statusToPrint]} font-medium py-1 px-3 text-sm`}
                            titleText='Milestone Status'
                            hoverText={statusToPrint}
                            content={`${milestone?.type ? milestone?.type : statusToPrint}`}
                        /> */}
                    </div>
                </div>
            })}
        </div>
    }
    return (
        <GridTable
            rowData={serviceRequestList.data}
            schema={filteredTableColumnsSchema}
            rowsPerPage={rowsPerPage}
            page={page}
            tablePagination
            count={memoizedServiceRequestCount}
            tablePaginationHandler={(limit, page) => updateQuery({ limit, page, filterSchema: ticketFilterSchema, filterList })}
            handleOnChangeCheckbox={() => { }}
            checkedTicketTracker={new Set()}
            isLoading={isServiceRequestListLoading}
            tableError='No Tickets Found'
        />
    );
};

export default TodayStatusTable;
